import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, MessageService, } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, CredentialsValidationError, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';

import { Endpoint } from '../../../constants/endpoints';
import { ActionType, InfusionScreenType, OptionCheck } from '../../../constants/infusion-solutions';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { ADEP_E1113Parameters, ADEP_E9129Parameters, FunctionName } from '../../../utils/parameters';
import { InfusionsViewerParams } from '../infusion-viewer/infusions-viewer-params';
import { AdminModalsService } from './admin-modals-service';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from './timeline-service';
import { BodyCheckInfo, FirstMedicationCheckInfoResponse } from '../eletronic-prescription/medication-viewer/medication-viewer-types';
import { Params } from '../eletronic-prescription/administration-schedule/administration-schedule-types';

export interface CreateFirstSolutionCheckRequest {
  actionType: number;
  infusionType: number;
  itemSequence: number;
  prescriptionNumber: number;
  scheduleSequence: number;
  userName?: string;
}

export default class InfusionService {

  public static validateInfusionProcess(body: Record<string, Value>): void {
    const promptConfig = {
      onDismiss: () => { },
      onError: this.createOnCredentialValidationErrorCallback(),
      onSuccess: async () => {
        InfusionService.startInfusion(body);
      },
      validateButtonExpression: MessageService.translate('$expression.$702951'),
    }

    const adepFunctionName = 'ADEP#9129';
    const confirmSecondCheck = FoundationUtils.findModuleParam(adepFunctionName, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);

    if (confirmSecondCheck === 'S') {
      foundationContext.promptCredentialsValidation(promptConfig);
    } else {
      InfusionService.startInfusion(body);
    }
  }

  public static createOnCredentialValidationErrorCallback(): (event: CredentialsValidationError) => void {
    let remainingTries = 3;

    return (event: CredentialsValidationError): void => {
      const { abort } = event;

      ToastService.open({
        dismissible: true,
        highlight: false,
        id: 'features.foundation-context.nok-toast',
        level: ToastLevel.ERROR,
        message: 'login-message.invalid-user-password-text',
        showIcon: true,
        type: ToastType.BANNER
      });

      if (remainingTries > 0) {
        remainingTries = remainingTries - 1;
      }

      if (remainingTries === 0) {
        AdminModalsService.redirectToTimeline('infusion-administration-by-schedule');
        applicationContext.popNavigationItem();
        abort();
      }
    };
  }

  public static startInfusion(body: Record<string, Value>): void {
    HttpService.post(Endpoint.EMAR_START_INFUSION, {}, { body })
      .then(response => {
        if (response.body) {
          const message = response.body as string;
          InfusionService.showToast(message, ToastLevel.ERROR);
        } else {
          InfusionService.handleStartInfusionSuccess();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => InfusionService.showToast(message, ToastLevel.ERROR));
        }
      });
  }

  private static showToast(message: string, toastLevel: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      id: generateUUID(),
      level: toastLevel,
      message: MessageService.translate(message),
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000
    });
  }

  public static goToTimeline(): void {
    InfusionsViewerParams.infusionScreenType === InfusionScreenType.ADMIN_BY_SCHEDULE
      ? AdminModalsService.redirectToTimeline('infusion-administration-by-schedule')
      : AdminModalsService.redirectToTimeline('infusion-administration-by-process');
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private static handleStartInfusionSuccess(): void {
    const timelineEvent = TimelineContext.get();
    const adepFunctionName = 'ADEP#9129';
    const confirmSecondCheck = FoundationUtils.findModuleParam(adepFunctionName, ADEP_E9129Parameters.CONFIRM_PERFORMING_SECOND_MEDICATION_CHECK);
    const firsCheckRealized = timelineEvent.status === 'CHECKED_FIRST';

    if (confirmSecondCheck === 'S' && !firsCheckRealized && InfusionsViewerParams.realizeFirstChecking) {
      this.goToTimeline();

      switch (InfusionsViewerParams.actionType) {
        case ActionType.START_SOLUTION:
          this.showToast('text.infusion-started-with-success', ToastLevel.SUCCESS);
          break;
        case ActionType.RESTART_SOLUTION:
          this.showToast('text.infusion-restarted-with-success', ToastLevel.SUCCESS);
          break;
        default:
          break;
      }
      return;
    }

    this.goToTimeline();
  }

  public static async validateStatusMedication(option: OptionCheck, bodyInfo?: BodyCheckInfo): Promise<boolean> {
    const isValidCheck = await this.getInfoCheckMedication(option, '', bodyInfo);
    return Promise.resolve(!!isValidCheck);
  }

  public static getInfoCheckMedication(option: OptionCheck, secondUser?: string, bodyInfo?: BodyCheckInfo): Promise<string> {
    const timelineEvent = TimelineContext.get();
    const param620 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOWS_YOU_TO_PERFORM_THE_FIRST_INFUSION_CHECK);

    if (timelineEvent.itemType === PrescriptionType.SOLUTION && param620 === Params.ALLOWED) {
      const timeLineEventMedication = timelineEvent as TimeLineEventMedication;
      const userName = secondUser ? secondUser : foundationContext.getUser()?.username;

      const bodyDefault = {
        itemCode: Number(timeLineEventMedication.itemSequence),
        itemType: timeLineEventMedication.itemType,
        option,
        prescriptionNumber: String(timeLineEventMedication.prescriptionNumber),
        scheduleSequence: Number(timeLineEventMedication.emarStepNumber),
        userName,
      }

      const body = bodyInfo ? bodyInfo : bodyDefault;

      return HttpService.post(Endpoint.EMAR_GET_INFO_FIRST_MEDICATION_CHECK, {}, { body })
        .then(response => {
          const responseBody = response.body as FirstMedicationCheckInfoResponse;
          return responseBody?.responseDescription;
        });
    }
    return Promise.resolve('');
  }

  public static createFirstInfusionCheck(request: CreateFirstSolutionCheckRequest): Promise<number> {
    const body = Object.assign({}, request) as unknown as Record<string, Value>;

    return HttpService.post(
      Endpoint.EMAR_CREATE_FIRST_INFUSION_CHECK, {},
      { body }
    ).then(response => {
      return response.status;
    });
  }
}
